import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fileMail, type UserPreferences } from '@/services/api/endpoints';
import { getItemAsEmlBase64ById } from '@/services/office/dragDrop';
import {
  applyFiledCategories,
  resetCategoryProbes,
  FILED_CATEGORY_NAME,
} from '@/services/office/categories';
import { destinationProjectNumber } from '@/features/file-mail/destinationNumber';
import { PREFERENCES_KEY } from '@/hooks/usePreferences';
import { createSourceDeleter } from '@/services/office/deleteFiledSource';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { reportError } from '@/services/errors/errorReporter';
import type { MailSummary } from '@/types/domain';
import type { SelectedItemInfo } from '@/services/office/selection';

interface FileMailArgs {
  /**
   * Dossier de destination, ou `null` pour la RACINE de l'espace.
   *
   * La racine n'est pas un cas dégradé : l'API la gère depuis toujours
   * (`targetContainerId` au lieu de `targetFolderId`), la page détail affiche les
   * mails qui y sont rangés, et c'est la seule destination possible dans un espace
   * qui n'a pas encore de dossier — cas où le sélecteur menait auparavant à une
   * impasse.
   */
  targetFolderId: string | null;
  /**
   * Projet ou espace auquel appartient `targetFolderId`.
   *
   * OBLIGATOIRE, et pas seulement utile : c'est lui qui porte le numéro d'affaire
   * de la catégorie « 00000 ». Le rendre facultatif laisserait ce chemin retrouver
   * le projet en fouillant les caches de dossiers — donc faire dépendre une
   * étiquette de ce qui a été AFFICHÉ. Les cinq points d'appel connaissent tous le
   * projet au moment du clic ; l'exiger fait porter la garantie par le type.
   */
  targetContainerId: string;
  items: SelectedItemInfo[];
  force?: boolean;
}

export interface DuplicateExisting {
  id: string;
  /** Readable folder name (empty = project root), never the ltree `path`. */
  folderName: string;
  filedAt: string;
  /** AAD oid of the filer — an identifier, never show it to a user. */
  filedBy: string;
  /** Display name of the filer. Null for rows filed before it was recorded. */
  filedByName?: string | null;
}

export interface FileMailItemResult {
  itemId: string;
  subject: string;
  status: 'created' | 'duplicate' | 'error';
  mailId?: string;
  errorMessage?: string;
  existing?: DuplicateExisting;
  /**
   * Pourquoi les catégories (« Rangé », numéro d'affaire) n'ont PAS pu être posées
   * sur le mail d'origine.
   *
   * UN SEUL MOTIF POUR LES DEUX ÉTIQUETTES, parce qu'un seul `addAsync` les porte :
   * elles arrivent ensemble ou pas du tout (cf. applyFiledCategories).
   *
   * Distinct de `errorMessage` à dessein : le rangement, lui, a réussi. Un échec
   * d'étiquetage ne doit donc pas faire passer le rangement pour raté — mais il
   * ne doit pas non plus disparaître, sinon l'absence d'étiquette reste
   * inexplicable pour l'utilisateur (typiquement sur une boîte partagée, où un
   * délégué n'a pas le droit de créer la catégorie).
   *
   * `undefined` quand les deux préférences sont désactivées, ou quand la seule
   * active ne visait rien (espace d'équipe sans numéro) : il n'y avait rien à faire.
   */
  categoryError?: string;
  /**
   * Pourquoi le mail source n'a PAS pu être déplacé vers les Éléments supprimés.
   *
   * Même logique que `categoryError` : le rangement a réussi, donc ce n'est pas
   * une erreur de rangement — mais le taire laisserait l'utilisateur croire sa
   * boîte nettoyée alors que le mail y est toujours.
   */
  deleteError?: string;
}

export function useFileMail() {
  const qc = useQueryClient();

  return useMutation<FileMailItemResult[], Error, FileMailArgs>({
    mutationFn: async ({ targetFolderId, targetContainerId, items, force }) => {
      const results: FileMailItemResult[] = [];

      // Préférences lues DANS le cache au moment du rangement, et non capturées au
      // rendu : le réglage peut avoir changé (autre fenêtre, autre poste) depuis
      // que ce composant a été monté. `undefined` — jamais chargé, ou back-end en
      // panne — vaut « désactivé », cf. usePreferences.
      const prefs = qc.getQueryData<UserPreferences>(PREFERENCES_KEY);

      // JEU DE CATÉGORIES calculé UNE FOIS pour tout le lot : la destination est
      // un dossier unique, donc le numéro d'affaire l'est aussi. Le résoudre par
      // mail relirait la même chose vingt fois.
      const names: string[] = [];
      if (prefs?.autoCategorize) names.push(FILED_CATEGORY_NAME);
      if (prefs?.categorizeProjectNumber) {
        const number = await destinationProjectNumber(qc, { containerId: targetContainerId });
        // `null` = espace d'équipe, ou projet sans numéro à cinq chiffres. Rien à
        // poser, et rien à signaler : il n'y avait pas d'étiquette à manquer.
        if (number) names.push(number);
      }

      // Une seule fois par LOT : un échec du lot précédent est oublié (il a pu
      // être transitoire), un succès est conservé. Cf. resetCategoryProbes.
      if (names.length > 0) resetCategoryProbes();

      /** Étiquette le mail source ; renvoie le motif d'échec, ou `undefined`. */
      const categorize = async (itemId: string): Promise<string | undefined> => {
        if (names.length === 0) return undefined;
        try {
          await applyFiledCategories(itemId, names);
          return undefined;
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          // Signalé, mais NON remonté en exception : le rangement a réussi et
          // c'est ce qui compte pour l'utilisateur.
          addBreadcrumb({
            category: 'office.event',
            message: 'apply-category failed',
            data: { message },
          });
          return message;
        }
      };

      /**
       * Déplace le mail source vers les Éléments supprimés. Jeton mutualisé sur
       * tout le lot, cf. createSourceDeleter.
       *
       * À n'appeler QUE sur un rangement RÉELLEMENT créé, jamais sur un doublon :
       * un doublon signifie que quelqu'un avait déjà rangé ce mail, et supprimer
       * la copie de l'utilisateur au motif d'une tentative redondante serait une
       * surprise désagréable — pour une action irréversible d'un clic.
       */
      const deleteSource = createSourceDeleter(
        qc.getQueryData<UserPreferences>(PREFERENCES_KEY)?.deleteAfterFiling ?? false,
      );

      for (const item of items) {
        addBreadcrumb({
          category: 'mutation',
          message: 'file-mail start',
          data: { folderId: targetFolderId, itemId: item.itemId.slice(0, 8) },
        });
        try {
          // Keep the item's own internetMessageId from getSelectedItems — it's
          // already the real RFC message-id — and ignore the helper's copy.
          const { base64 } = await getItemAsEmlBase64ById(item.itemId);
          const resp = await fileMail({
            format: 'eml',
            contentBase64: base64,
            // L'UN OU L'AUTRE, jamais les deux : le serveur distingue « dans ce
            // dossier » de « à la racine de cet espace » par la clé présente.
            ...(targetFolderId ? { targetFolderId } : { targetContainerId }),
            metadata: {
              subject: item.subject,
              internetMessageId: item.internetMessageId,
              conversationId: item.conversationId,
            },
            force,
          });
          // Étiquetage APRÈS confirmation du serveur, jamais avant : la catégorie
          // affirme « ce mail est dans l'outil ».
          //
          // Posée aussi sur un DOUBLON : le mail est bel et bien rangé (il l'était
          // déjà), donc l'étiquette est vraie. La refuser ici laisserait sans
          // pastille verte, définitivement, tout mail rangé avant l'activation de
          // la préférence — et ré-essayer serait justement le geste naturel.
          const categoryError = await categorize(item.itemId);
          if (resp.status === 'created') {
            // Suppression EN DERNIER : après l'étiquetage, pour que le mail parte
            // aux Éléments supprimés déjà marqué « Rangé » si l'utilisateur a
            // activé les deux — et surtout après la confirmation du serveur.
            const deleteError = await deleteSource(item.itemId);
            results.push({
              itemId: item.itemId,
              subject: item.subject,
              status: 'created',
              mailId: resp.mailId,
              categoryError,
              deleteError,
            });
          } else {
            results.push({
              itemId: item.itemId,
              subject: item.subject,
              status: 'duplicate',
              existing: resp.existing,
              categoryError,
            });
          }
        } catch (err) {
          reportError({
            error: err instanceof Error ? err : new Error(String(err)),
            tags: ['file-mail', 'item'],
          });
          results.push({
            itemId: item.itemId,
            subject: item.subject,
            status: 'error',
            errorMessage: err instanceof Error ? err.message : String(err),
          });
        }
      }
      return results;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders'] });
      // PRÉFIXE `['mails']` et non la clé exacte : les mails d'un dossier
      // (`['mails', folderId]`) et ceux d'une racine (`['mails', 'root', cid]`)
      // sont deux clés distinctes, et viser la première laissait la liste d'une
      // racine périmée après un rangement à la racine.
      qc.invalidateQueries({ queryKey: ['mails'] });
    },
  });
}

export type { MailSummary };
